import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Package, TrendingDown, AlertCircle, RefreshCw, LogOut, ScanBarcode, ChevronRight, Moon, Sun, Settings } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { getDashboardStats, getRecentActivity } from '../lib/api';
import { Button } from '../components/ui/button';

const REASON_LABELS = {
  walk_in_sale: 'Walk-in Sale',
  online_sale: 'Online Sale',
  sampling: 'Sampling',
  breakage: 'Breakage',
  other: 'Other',
  restock: 'Restock',
};

const DashboardPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, activityRes] = await Promise.all([
        getDashboardStats(),
        getRecentActivity(5),
      ]);
      setStats(statsRes.data);
      setActivity(activityRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-enter" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-heading font-bold text-foreground">
            Hello, {user?.name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {user?.role === 'admin' ? 'Administrator' : 'Staff'}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={toggleTheme}
            className="btn-icon text-muted-foreground"
            data-testid="theme-toggle-btn"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          {isAdmin() && (
            <button
              onClick={() => navigate('/settings')}
              className="btn-icon text-muted-foreground"
              data-testid="settings-btn"
              aria-label="Settings"
            >
              <Settings className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={handleLogout}
            className="btn-icon text-muted-foreground"
            data-testid="logout-btn"
            aria-label="Logout"
          >
            <LogOut className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Quick Action */}
      <Button
        onClick={() => navigate('/stock-out')}
        className="btn-primary-action"
        data-testid="quick-stock-out-btn"
      >
        <ScanBarcode className="w-5 h-5" />
        <span>Log Stock Out</span>
      </Button>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-4">
        <div className="stat-card" data-testid="stat-products">
          <Package className="w-5 h-5 text-primary" />
          <span className="data-value">{stats?.active_products || 0}</span>
          <span className="data-label">Active Products</span>
        </div>
        <div className="stat-card" data-testid="stat-sold-today">
          <TrendingDown className="w-5 h-5 text-destructive" />
          <span className="data-value">{stats?.items_sold_today || 0}</span>
          <span className="data-label">Sold Today</span>
        </div>
        {isAdmin() && (
          <div 
            className="stat-card col-span-2 cursor-pointer touch-feedback hover:border-warning/50 transition-colors"
            onClick={() => navigate('/restock')}
            data-testid="stat-restock"
          >
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-warning" />
                <span className="data-value">{stats?.items_needing_restock || 0}</span>
              </div>
              <ChevronRight className="w-5 h-5 text-muted-foreground" />
            </div>
            <span className="data-label">Items Need Restock</span>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div>
        <h2 className="text-base font-semibold font-heading mb-3">Recent Activity</h2>
        {activity.length === 0 ? (
          <div className="empty-state py-8">
            <Package className="empty-state-icon w-12 h-12" />
            <p className="empty-state-text">No recent activity</p>
          </div>
        ) : (
          <div className="space-y-2">
            {activity.map((item) => (
              <div 
                key={item.id} 
                className="item-card flex items-center gap-3"
                data-testid={`activity-${item.id}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                  item.movement_type === 'stock_out' ? 'bg-destructive/10' : 'bg-success/10'
                }`}>
                  <span className={`font-data font-bold ${
                    item.movement_type === 'stock_out' ? 'text-destructive' : 'text-success'
                  }`}>
                    {item.movement_type === 'stock_out' ? '-' : '+'}{item.quantity}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm truncate">{item.product_name}</p>
                  <p className="text-xs text-muted-foreground">
                    <span className="font-data">{item.product_sku}</span>
                    {' · '}
                    {REASON_LABELS[item.reason] || item.reason}
                  </p>
                </div>
                <span className="text-xs text-muted-foreground font-data">
                  {formatTime(item.created_at)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DashboardPage;
